import { Box, Icon, Skeleton, Typography, useMediaQuery } from '@mui/material'
import StatusBar from 'components/reusable/statusBar/AdxStatusBar'
import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { getAppliedCourse } from 'services/api/modules/upskill'
import { format } from 'date-fns'
import CourseRequestDrawer from './CourseRequestDrawer'
import AdxSortTable from 'components/reusable/table/AdxSortTable'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const CourseCard = () => {
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  const [drawerState, setDrawerState] = useState({ right: false })
  const [data, setData] = useState(null)

  const handleDrawer = (anchor, open) => (item) => {
    setDrawerState({ ...drawerState, [anchor]: open })
    setData(item)
  }

  // pagination
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const {
    isLoading: courseIsLoading,
    error: courseIsError,
    data: courseData,
  } = useQuery(
    ['courseData', rowsPerPage, page],
    async () => {
      const { data } = await getAppliedCourse(rowsPerPage, page + 1)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const getStatusText = (status) => {
    switch (status) {
      case 'Pending':
        return 'PENDING'
      case 'Approved':
        return 'APPROVED'
      case 'Rejected':
        return 'REJECTED'
      default:
        return '' // Default case, return an empty string or another default value
    }
  }

  const formatDate = (inputDate) => {
    const formattedDate = format(new Date(inputDate), 'dd/MM/yyyy')
    return formattedDate
  }

  const courseColumn = [
    {
      Header: 'Course Details',
      accessor: 'course_name',
      Cell: (props) => {
        const {
          row: { original },
        } = props

        return (
          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
            <Box display={'flex'} alignItems={'center'} gap={isLarge ? '22px' : '12px'}>
              <Box>
                <Box
                  height={'35px'}
                  width={'35px'}
                  borderRadius={'100%'}
                  bgcolor={'alternate.main'}
                  color={'white.main'}
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  <Typography variant="h4">{original.course_name.charAt(0)}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant={isLarge ? 'subtitle1' : 'subtitle2'} mb={'4px'}>
                  {original.course_name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {!isLarge && <StatusBar status={getStatusText(original.status)} />}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>circle</Icon>
                    <Typography variant="body2" color="light-gray-text.main">
                      Applied on {formatDate(original.apply_date)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Icon
              sx={{
                fontSize: 30,
                color: 'light-gray-text.main',
                display: isLarge ? 'none' : 'block',
              }}
            >
              chevron_right
            </Icon>
          </Box>
        )
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: (props) => {
        const { value } = props

        return (
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Icon sx={{ fontSize: 30, visibility: 'hidden' }}>chevron_right</Icon>
            <StatusBar status={getStatusText(value)} sx={{ width: '150px' }} />
            <Icon sx={{ fontSize: 30, color: 'light-gray-text.main' }}>chevron_right</Icon>
          </Box>
        )
      },
    },
  ]

  return (
    <React.Fragment>
      <Box mt={isLarge ? '24px' : '16px'}>
        <Typography color="dark-text.main" variant={isLarge ? 'h4' : 'subtitle1'} noWrap>
          My Course Request
        </Typography>
        {courseIsLoading ? (
          <Box mt={isLarge ? '20px' : '8px'}>
            {Array.from({ length: 3 }, (_, index) => (
              <Box
                key={index}
                bgcolor="white.main"
                paddingX={'20px'}
                paddingY={'24px'}
                sx={{
                  borderBottom: '1px solid',
                  borderBottomColor: 'gray-line.main',
                  cursor: 'pointer',
                }}
                borderRadius={
                  index === 0 ? '4.5px 4.5px 0px 0px' : index === 2 ? ' 0px 0px 4.5px 4.5px' : ''
                }
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display={'flex'} alignItems="center" gap="10px">
                  <Skeleton variant="circular" width={'35px'} height={'35px'} />
                  <Box>
                    <Typography variant="subtile1">
                      <Skeleton variant="text" width={'150px'} />
                    </Typography>
                    <Box display="flex" alignItems="center" columnGap={1}>
                      <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>circle</Icon>
                      <Typography variant="body2" color="light-gray-text.main">
                        <Skeleton variant="text" width={'80px'} />
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" columnGap={1}>
                  <Skeleton variant="rounded" width={'100px'} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : courseIsError || courseData?.data?.length === 0 ? (
          <Box mt={isLarge ? '20px' : '8px'}>
            <AdxEmptyStateV2 icon={<Icon>error</Icon>} title="No course requested" />
          </Box>
        ) : courseData?.data?.length > 0 ? (
          <Box mt={isLarge ? '20px' : '8px'} bgcolor={'white.main'}>
            <AdxSortTable
              column={isLarge ? courseColumn : courseColumn.splice(0, 1)}
              data={courseData?.data}
              onClick={handleDrawer('right', true)}
              divClick
              sx={{ boxShadow: 'none', border: '1px solid #E5E5E5' }}
              sxCell={{ padding: isLarge ? '18px 30px' : '20px' }}
              pagination
              count={courseData?.meta?.total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        ) : null}
      </Box>
      <CourseRequestDrawer
        drawerState={drawerState}
        handleDrawer={handleDrawer}
        data={data}
        getStatusText={getStatusText}
      />
    </React.Fragment>
  )
}

export default CourseCard
